import React from 'react';
import Serializer, {
  type SerializeArguments,
  type DeserializeArguments,
} from './Serializer';
import { isList } from '../../plugins/ListPlugin/guards';
import {
  isEmptyContainerNode,
  isHTMLElementNode,
} from '../helpers/serializerHelpers';

/*
 * A <br> tag is an invalid child of an <ol> or <ul>... but sometimes
 * we get some rebels that include them anyway.
 */
function removeInvalidBrTagChildren(children: Element[]) {
  return children.filter((child) => child.tagName.toLowerCase() !== 'br');
}

class SerializerList extends Serializer {
  static serialize({ node, children }: SerializeArguments): React.ReactNode {
    if (!isList(node)) return;

    let listElement: React.ReactNode;

    switch (node.type) {
      case 'ul_list':
        listElement = <ul>{children}</ul>;
        break;
      case 'ol_list':
        listElement = <ol>{children}</ol>;
        break;
      case 'dl_list':
        listElement = <dl>{children}</dl>;
        break;
      case 'checklist':
        listElement = <ul className="checklist">{children}</ul>;
        break;
      default:
        break;
    }

    return listElement;
  }

  static deserialize({ el, markAttributes, next }: DeserializeArguments) {
    if (
      !isHTMLElementNode(el) ||
      !['ul', 'ol', 'dl'].includes(el.tagName.toLowerCase())
    )
      return;
    let type: 'ul_list' | 'ol_list' | 'dl_list' | 'checklist' = 'ul_list';

    switch (el.tagName.toLowerCase()) {
      case 'ul':
        type = 'ul_list';
        break;
      case 'ol':
        type = 'ol_list';
        break;
      case 'dl':
        type = 'dl_list';
        break;
    }

    // Ignore lists that only contain whitespace
    if (isEmptyContainerNode(el)) {
      return;
    }

    const children = removeInvalidBrTagChildren(Array.from(el.children));

    if (children.length === 0) {
      return [];
    }

    if (el.classList.contains('checklist')) {
      type = 'checklist';
    }

    const hasCheckbox = children.some(
      (el) =>
        el.classList.contains('checklist-item--checked') ||
        el.classList.contains('checklist-item--unchecked')
    );
    if (hasCheckbox) type = 'checklist';

    return {
      type,
      children: next(children, markAttributes),
    };
  }
}

export default SerializerList;
